/**
 * Design tokens — ONE semantic token module for BOTH shells.
 * Lane va-ui-design-tokens-2026-10-01 (issue #2196).
 *
 * Why this file exists (measured 2026-10-01, desktop 1680 + mobile 390 scans of prod):
 * the desktop shell rendered a beige-on-beige empty state while the mobile shell was
 * dark navy — two visual applications for one product. This module is the foundation
 * of the unification: ONE small token source drives CSS custom properties that BOTH
 * the desktop and the mobile shell stamp on their shell root, so the two forks can
 * never drift apart again (the drift is pinned by
 * `setpoint-evals/ui-design-tokens/`).
 *
 * Location — deliberately at the src ROOT (sibling of `brands.ts`): the platform
 * firewall (`frontend/eslint.config.mjs`) forbids `mobile/**` ↔ `desktop/**` imports
 * and forbids the shared layer from importing either fork. A src-root module is
 * importable by BOTH shells without touching the firewall.
 *
 * Shape:
 *   - surface/text/accent/danger COLOR families, dark-first (`dark` is the shared
 *     default face; `light` exists as a variant — runbook R-004).
 *   - spacing scale 4/8/12/16/24, radius, type scale (constant across variants;
 *     the desktop density modes retune their own `--desk-*` layer on top — never
 *     colors — and are unaffected).
 *   - `applyDesignTokens(el)` stamps the families as `--va-*` CSS custom properties
 *     on a shell root + marks it `data-va-variant`. CSS custom properties inherit,
 *     so every descendant rule can consume `var(--va-*)`. It stamps `document.body`
 *     the same way (issue #2307): a surface a shell opens outside its own root
 *     inherits the shell's family from there (see `applyDesignTokens` below).
 *
 * Consumption scope (this lane): shell level only. The desktop shell remaps its
 * legacy theme names from the family inside `[data-shell='desktop']` (desktop.css);
 * the mobile shell chrome bands consume the tokens directly (style.css). Per-component
 * restyles are follow-up lanes (va-ui-desktop-home, va-ui-composer-fsm,
 * va-ui-mobile-token-unify). The legacy 4-theme switcher keeps working for the
 * mobile chat content and the standalone pages — zero VALKO_PROFILE delta.
 *
 * Setpoint-pinned: `setpoint-evals/ui-design-tokens/support/token-contract.ts`
 * carries the same values — keep the two in lockstep (SE-01 greps this file for
 * every pinned value).
 */
export type DesignTokenVariant = 'dark' | 'light';

/** The semantic families. Keys are semantic (s1 = base canvas, s2 = raised chrome …). */
export interface DesignTokenFamily {
  /** Surface family: base canvas, raised chrome, elevated popovers/inputs. */
  surface: { s1: string; s2: string; s3: string };
  /** Text family: primary, secondary. */
  text: { t1: string; t2: string };
  /** Accent — actions, focus, selection (the brato teal on dark, a AA-safe teal on light). */
  accent: string;
  /** Danger — destructive actions only. */
  danger: string;
  /** Spacing scale: 4 / 8 / 12 / 16 / 24 px. */
  spacing: { s1: string; s2: string; s3: string; s4: string; s5: string };
  /** Radius scale. */
  radius: { s: string; m: string; l: string };
  /** Type scale. */
  type: { xs: string; sm: string; body: string; title: string };
}

/**
 * THE token source. Dark is the shared face (default); light is the variant
 * (the former zen-paper family, kept for the future light-variant toggle).
 */
export const DESIGN_TOKENS: Record<DesignTokenVariant, DesignTokenFamily> = {
  dark: {
    surface: { s1: '#101214', s2: '#171a1f', s3: '#1e2228' },
    text: { t1: '#e8eaed', t2: '#9aa0a8' },
    accent: '#4ec9b0',
    danger: '#e5484d',
    spacing: { s1: '4px', s2: '8px', s3: '12px', s4: '16px', s5: '24px' },
    radius: { s: '4px', m: '8px', l: '12px' },
    type: { xs: '11px', sm: '12px', body: '14px', title: '16px' },
  },
  light: {
    surface: { s1: '#e8e2d8', s2: '#dfd8cd', s3: '#f2ebe1' },
    text: { t1: '#3a3630', t2: '#6f6a62' },
    accent: '#0f766e',
    danger: '#b3261e',
    spacing: { s1: '4px', s2: '8px', s3: '12px', s4: '16px', s5: '24px' },
    radius: { s: '4px', m: '8px', l: '12px' },
    type: { xs: '11px', sm: '12px', body: '14px', title: '16px' },
  },
};

/** A full `--va-*` custom-property map for one variant. */
export type DesignTokenCss = Record<`--va-${string}`, string>;

/** Derive the CSS custom-property map from the token source (one direction only). */
export function buildTokenCss(variant: DesignTokenVariant): DesignTokenCss {
  const t = DESIGN_TOKENS[variant];
  return {
    '--va-surface-1': t.surface.s1,
    '--va-surface-2': t.surface.s2,
    '--va-surface-3': t.surface.s3,
    '--va-text-1': t.text.t1,
    '--va-text-2': t.text.t2,
    '--va-accent': t.accent,
    '--va-danger': t.danger,
    '--va-space-1': t.spacing.s1,
    '--va-space-2': t.spacing.s2,
    '--va-space-3': t.spacing.s3,
    '--va-space-4': t.spacing.s4,
    '--va-space-5': t.spacing.s5,
    '--va-radius-s': t.radius.s,
    '--va-radius-m': t.radius.m,
    '--va-radius-l': t.radius.l,
    '--va-type-xs': t.type.xs,
    '--va-type-sm': t.type.sm,
    '--va-type-body': t.type.body,
    '--va-type-title': t.type.title,
  };
}

/** Stamp one variant's families on an element as CSS custom properties and mark it (`data-va-variant`). Idempotent. */
function stampFamily(el: HTMLElement, variant: DesignTokenVariant): void {
  const css = buildTokenCss(variant);
  for (const [name, value] of Object.entries(css)) {
    el.style.setProperty(name, value);
  }
  el.setAttribute('data-va-variant', variant);
}

/**
 * Stamp a variant's families onto a shell root as CSS custom properties and mark
 * the root (`data-va-variant`). Called from both shells' root ref — runs before
 * paint, so there is no flash of un-tokened chrome. Idempotent.
 *
 * The same stamp goes on `document.body` (issue #2307). A surface a shell opens is
 * not always mounted inside the shell root: a `createPortal(…, document.body)` (the
 * composer attach drawer, the PlansTab confirm) sits beside `#app`, and the credits
 * surface (balance pill, top-up modal) is mounted next to the shell by App.tsx. They
 * inherit from body, so their legacy theme names resolved against the LIGHT `:root`
 * Zen palette — a cream sheet over the dark chat. With the family on body,
 * `shell-token-remap.css` re-points those names there as it does on the shell root.
 * The standalone pages mount no shell and never call this, so body stays unstamped
 * and they keep the Zen theme switcher. The stamp lives as long as the document: a
 * shell is never torn down for a standalone page, those are full page loads.
 */
export function applyDesignTokens(el: HTMLElement, variant: DesignTokenVariant = 'dark'): void {
  stampFamily(el, variant);
  const host = el.ownerDocument?.body;
  if (host && host !== el) stampFamily(host, variant);
}
